import { useState, useMemo, type ReactNode } from "react";
import { AlertTriangle, Download, Eye, Loader2, Paperclip, Search, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Ticket, formatTicketTypeLabelCompact } from "@/types/ticket";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  formatStatusLabel,
  formatStatusLabelCompact,
  normalizeStatus,
  statusColors,
} from "@sd/constants/ticketStatuses";
import { looksLikeOpaqueUserId } from "@sd/lib/ticketRowMapping";
import { getUserDisplayFromMap } from "@sd/lib/userDisplayMap";
import { useServiceDeskUserDisplayMap } from "@sd/hooks/useServiceDeskUserDisplayMap";
import { useAuth } from "@sd/hooks/useAuth";
import { isExternalSdRole, isSuperAgentRole } from "@sd/lib/sdRoles";
import { downloadAttachmentByFileId } from "@sd/lib/attachmentMediaUrl";
import { notify } from "@/components/common/Notification";

/** Super Agent stream: match detail dropdown — "Pending From Client", not compact "Pending Client". */
function streamStatusLabel(status: unknown, isSuperAgent: boolean): string {
  if (isSuperAgent && normalizeStatus(status) === "pending_from_client") {
    return formatStatusLabel(status);
  }
  return formatStatusLabelCompact(status);
}

interface TicketListProps {
  tickets: Ticket[];
  selectedTicketId: string | null;
  onSelectTicket: (ticketId: string) => void;
  selectionMode?: boolean;
  selectedTickets?: Set<string>;
  onToggleSelection?: (ticketId: string) => void;
  onToggleSelectAll?: () => void;
  /** When true, STREAM column search is hidden — parent supplies pre-filtered tickets. */
  hideStreamSearch?: boolean;
  /** When true, pagination handles navigation — no inner scroll, compact rows. */
  paginated?: boolean;
  /** Backend total (`count` from API) — shown in header corner when set. */
  totalCount?: number;
  /** Opens read-only full ticket detail page (e.g. Super Agent). */
  onViewTicket?: (ticketId: string) => void;
}

const ticketTypeColors: Record<string, string> = {
  incident: "bg-red-50 text-red-700 border-red-200/60 font-bold rounded-md",
  request: "bg-blue-50 text-blue-700 border-blue-200/60 font-bold rounded-md",
  access: "bg-amber-50 text-amber-700 border-amber-250/60 font-bold rounded-md",
  issue: "bg-slate-50 text-slate-700 border-slate-200/60 font-bold rounded-md",
  new_requirement:
    "bg-violet-50 text-violet-700 border-violet-200/60 font-bold rounded-md",
  suggestion:
    "bg-emerald-50 text-emerald-700 border-emerald-200/60 font-bold rounded-md",
};

function ticketTypeLabel(type: Ticket["ticketType"], raw?: string): string {
  return formatTicketTypeLabelCompact(type, raw);
}

function streamAttachmentIds(ticket: Ticket): { id: string; name: string }[] {
  if (ticket.attachments && ticket.attachments.length > 0) {
    return ticket.attachments.map(item => ({
      id: item.id,
      name: item.name || "File",
    }));
  }
  return (ticket.attachmentFileIds ?? []).map((id, index) => ({
    id,
    name: `File ${index + 1}`,
  }));
}

function StreamAttachmentDownloads({ ticket }: { ticket: Ticket }) {
  const files = streamAttachmentIds(ticket);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  if (files.length === 0) {
    return null;
  }

  const downloadOne = async (fileId: string) => {
    setDownloadingId(fileId);
    try {
      const ok = await downloadAttachmentByFileId(fileId);
      if (!ok) {
        notify.error("Download failed", "Could not download this file.");
      }
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div
      className="mt-1.5 flex flex-wrap items-center gap-1.5"
      onClick={event => event.stopPropagation()}
    >
      <Paperclip className="h-3 w-3 shrink-0 text-muted-foreground" />
      {files.map(file => (
        <button
          key={file.id}
          type="button"
          className="inline-flex max-w-full items-center gap-1 rounded-md border border-border bg-background px-1.5 py-0.5 text-[10px] font-semibold text-foreground hover:bg-muted disabled:opacity-60"
          title={`Download ${file.name}`}
          disabled={downloadingId === file.id}
          onClick={() => {
            void downloadOne(file.id);
          }}
        >
          {downloadingId === file.id ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Download className="h-3 w-3" />
          )}
          <span className="truncate">{file.name}</span>
        </button>
      ))}
    </div>
  );
}

/** STREAM listing: empty/null → `emptyLabel`, UUID → display name. */
function listingDisplayName(
  raw: string | undefined,
  userDisplayMap: Map<string, string>,
  agentEmployees: { id: string; name: string }[],
  emptyLabel: string,
  hideOpaqueId = false
): string {
  const t = (raw ?? "").trim();
  if (!t || t === "—" || t === "–" || t === "-" || t.toLowerCase() === "null") {
    return emptyLabel;
  }
  if (looksLikeOpaqueUserId(t)) {
    return (
      getUserDisplayFromMap(userDisplayMap, t) ??
      agentEmployees.find(a => a.id === t)?.name ??
      (hideOpaqueId ? emptyLabel : t)
    );
  }
  return t;
}

function StreamPersonLine({
  role,
  name,
  assigned = false,
}: {
  role: string;
  name: string;
  assigned?: boolean;
}) {
  const unassigned = assigned && name === "Unassigned";
  return (
    <p
      className="flex min-w-0 items-baseline gap-1.5 text-xs leading-snug"
      title={`${role}: ${name}`}
    >
      <span className="shrink-0 font-semibold text-slate-500">{role}:</span>
      <span
        className={cn(
          "min-w-0 truncate font-medium",
          unassigned
            ? "italic text-slate-500"
            : assigned
              ? "text-primary"
              : "text-foreground/80"
        )}
      >
        {name}
      </span>
    </p>
  );
}

function StreamPeopleAndStatus({
  requesterLabel,
  assigneeLabel,
  projectLabel,
  showProject = false,
  status,
  hideAssignee = false,
}: {
  requesterLabel: string;
  assigneeLabel: string;
  projectLabel?: string;
  showProject?: boolean;
  status: ReactNode;
  hideAssignee?: boolean;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-start justify-between gap-2">
        <StreamPersonLine role="Requester" name={requesterLabel} />
        {status}
      </div>
      {hideAssignee ? null : (
        <StreamPersonLine role="Assigned" name={assigneeLabel} assigned />
      )}
      {showProject && projectLabel ? (
        <StreamPersonLine role="Project" name={projectLabel} />
      ) : null}
    </div>
  );
}

function ticketRowClass(isSelected: boolean, gapClass: string) {
  return cn(
    "relative flex w-full cursor-pointer border-b border-border/40 p-4 text-left transition-all duration-150",
    gapClass,
    isSelected
      ? "z-[1] border-l-4 border-l-slate-900 bg-slate-100 shadow-sm ring-1 ring-inset ring-slate-200/90"
      : "border-l-4 border-l-transparent hover:bg-muted/40"
  );
}

function ticketIdClass(isSelected: boolean) {
  return cn(
    "shrink-0 font-mono text-xs font-bold",
    isSelected ? "text-slate-900" : "text-slate-600"
  );
}

function ticketTitleClass(isSelected: boolean) {
  return cn(
    "mb-1.5 line-clamp-3 break-words text-sm leading-snug transition-colors",
    isSelected
      ? "font-semibold text-slate-900"
      : "font-medium text-muted-foreground"
  );
}

export function TicketList({
  tickets,
  selectedTicketId,
  onSelectTicket,
  selectionMode = false,
  selectedTickets = new Set(),
  onToggleSelection,
  onToggleSelectAll,
  hideStreamSearch = false,
  paginated = false,
  totalCount,
  onViewTicket,
}: TicketListProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const { role } = useAuth();
  const hideAssignee = isExternalSdRole(role);
  const isSuperAgent = isSuperAgentRole(role);
  const showProjectName =
    role === "ServiceDesk_Agent" ||
    isSuperAgentRole(role) ||
    isExternalSdRole(role);
  const { userDisplayMap, agentEmployees } = useServiceDeskUserDisplayMap({
    refetchAgentListsOnMount: false,
  });

  // Full-text search across ticket fields (skipped when parent filters — see hideStreamSearch)
  const searchedTickets = useMemo(() => {
    if (hideStreamSearch) return tickets;
    if (!searchQuery.trim()) return tickets;

    const query = searchQuery.toLowerCase();
    return tickets.filter(ticket => {
      // Search ID
      if (ticket.id.toLowerCase().includes(query)) return true;
      if (ticket.uiId?.toLowerCase().includes(query)) return true;
      // Search title
      if (ticket.title.toLowerCase().includes(query)) return true;
      // Search description
      if (ticket.description?.toLowerCase().includes(query)) return true;
      // Search user
      if (ticket.user.name.toLowerCase().includes(query)) return true;
      if (ticket.user.company.toLowerCase().includes(query)) return true;
      if (ticket.assignee?.toLowerCase().includes(query)) return true;
      // Search notes/comments
      if (ticket.notes.some(n => n.content.toLowerCase().includes(query)))
        return true;
      // Search attachments
      if (ticket.attachments?.some(a => a.name.toLowerCase().includes(query)))
        return true;
      // Search category
      if (ticket.category?.toLowerCase().includes(query)) return true;
      if (ticket.projectName?.toLowerCase().includes(query)) return true;

      return false;
    });
  }, [tickets, searchQuery, hideStreamSearch]);

  return (
    <div
      className={cn(
        "flex h-full min-h-0 flex-col bg-card",
        !paginated && "border-r border-border"
      )}
    >
      {/* Header — aligned with TicketDetail + UserPanel (h-14) */}
      <div className="shrink-0 border-b border-border bg-slate-100/50">
        <div className="flex h-14 min-h-14 items-center justify-between px-4">
          <div className="flex items-center gap-2">
            {selectionMode ? (
              <Checkbox
                checked={
                  searchedTickets.length > 0 &&
                  searchedTickets.every(t => selectedTickets.has(t.id))
                }
                onCheckedChange={() => onToggleSelectAll?.()}
                aria-label="Select all tickets on this page"
              />
            ) : null}
            <span className="text-sm font-black uppercase tracking-wide text-slate-900">
              STREAM
            </span>
            {selectionMode && selectedTickets.size > 0 && (
              <Badge variant="secondary" className="text-xs">
                {selectedTickets.size} selected
              </Badge>
            )}
          </div>
          <Badge
            variant="secondary"
            className="rounded-md border-slate-300 bg-slate-200 px-2.5 py-0.5 text-sm font-black tabular-nums text-slate-800"
          >
            {totalCount !== undefined
              ? totalCount
              : hideStreamSearch
                ? "…"
                : searchedTickets.length}
          </Badge>
        </div>

        {!hideStreamSearch ? (
          <div className="px-4 pb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search tickets..."
                className="h-8 pl-9 pr-8 text-sm"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2"
                  onClick={() => setSearchQuery("")}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
            {searchQuery && (
              <p className="mt-2 text-xs text-muted-foreground">
                Found {searchedTickets.length} ticket
                {searchedTickets.length !== 1 ? "s" : ""} matching "
                {searchQuery}"
              </p>
            )}
          </div>
        ) : null}
      </div>

      {paginated ? (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="divide-y divide-border/60">
            {searchedTickets.map(ticket => {
              const requesterLabel = listingDisplayName(
                ticket.user.name,
                userDisplayMap,
                agentEmployees,
                "—",
                hideAssignee
              );
              const assigneeLabel = listingDisplayName(
                ticket.assignee,
                userDisplayMap,
                agentEmployees,
                "Unassigned"
              );
              const isSelected = selectedTicketId === ticket.id;
              return (
                <div
                  key={ticket.id}
                  className={ticketRowClass(isSelected, "gap-2")}
                  onClick={() => onSelectTicket(ticket.id)}
                >
                  {selectionMode && (
                    <div className="pt-0.5" onClick={e => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedTickets.has(ticket.id)}
                        onCheckedChange={() => onToggleSelection?.(ticket.id)}
                      />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex items-start justify-between gap-1.5">
                      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                        <span className={ticketIdClass(isSelected)}>
                          {ticket.uiId ?? ticket.id}
                        </span>
                        {ticket.overdueLevel === "critical" && (
                          <AlertTriangle className="h-4 w-4 shrink-0 fill-red-500/10 text-red-500" />
                        )}
                        {ticket.isVip && (
                          <Badge className="border-amber-250 shadow-3xs bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-800">
                            VIP
                          </Badge>
                        )}
                        <Badge
                          variant="outline"
                          className={cn(
                            "shadow-3xs border px-2 py-0.5 text-xs",
                            ticketTypeColors[ticket.ticketType]
                          )}
                        >
                          {ticketTypeLabel(
                            ticket.ticketType,
                            ticket.ticketTypeRaw
                          )}
                        </Badge>
                      </div>
                      {onViewTicket ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 shrink-0"
                          title="View ticket details (full page)"
                          onClick={e => {
                            e.stopPropagation();
                            onViewTicket(ticket.id);
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      ) : null}
                    </div>
                    <h3
                      className={ticketTitleClass(isSelected)}
                      title={ticket.title}
                    >
                      {ticket.title}
                    </h3>
                    <StreamPeopleAndStatus
                      requesterLabel={requesterLabel}
                      assigneeLabel={assigneeLabel}
                      projectLabel={ticket.projectName}
                      showProject={showProjectName}
                      hideAssignee={hideAssignee}
                      status={
                        <Badge
                          variant="outline"
                          className={cn(
                            "shadow-3xs shrink-0 whitespace-nowrap px-2.5 py-0.5 text-xs font-bold",
                            statusColors[
                              ticket.status as keyof typeof statusColors
                            ] ?? statusColors.new
                          )}
                          title={formatStatusLabel(ticket.status)}
                        >
                          {streamStatusLabel(ticket.status, isSuperAgent)}
                        </Badge>
                      }
                    />
                    <StreamAttachmentDownloads ticket={ticket} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <ScrollArea className="min-h-0 flex-1">
          <div className="divide-y divide-border/60">
            {searchedTickets.map(ticket => {
              const requesterLabel = listingDisplayName(
                ticket.user.name,
                userDisplayMap,
                agentEmployees,
                "—",
                hideAssignee
              );
              const assigneeLabel = listingDisplayName(
                ticket.assignee,
                userDisplayMap,
                agentEmployees,
                "Unassigned"
              );
              const isSelected = selectedTicketId === ticket.id;
              return (
                <div
                  key={ticket.id}
                  className={ticketRowClass(isSelected, "gap-3")}
                  onClick={() => onSelectTicket(ticket.id)}
                >
                  {selectionMode && (
                    <div className="pt-0.5" onClick={e => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedTickets.has(ticket.id)}
                        onCheckedChange={() => onToggleSelection?.(ticket.id)}
                      />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-1.5">
                      <span className={ticketIdClass(isSelected)}>
                        {ticket.uiId ?? ticket.id}
                      </span>
                      {ticket.overdueLevel === "critical" && (
                        <AlertTriangle className="h-4 w-4 shrink-0 fill-red-500/10 text-red-500" />
                      )}
                      {ticket.isVip && (
                        <Badge className="border-amber-250 shadow-3xs bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-800">
                          VIP
                        </Badge>
                      )}
                      <Badge
                        variant="outline"
                        className={cn(
                          "shadow-3xs border px-2 py-0.5 text-xs",
                          ticketTypeColors[ticket.ticketType]
                        )}
                      >
                        {ticketTypeLabel(
                          ticket.ticketType,
                          ticket.ticketTypeRaw
                        )}
                      </Badge>
                    </div>
                    <h3
                      className={ticketTitleClass(isSelected)}
                      title={ticket.title}
                    >
                      {ticket.title}
                    </h3>
                    <StreamPeopleAndStatus
                      requesterLabel={requesterLabel}
                      assigneeLabel={assigneeLabel}
                      projectLabel={ticket.projectName}
                      showProject={showProjectName}
                      hideAssignee={hideAssignee}
                      status={
                        <div className="flex shrink-0 items-center gap-1.5">
                          <Badge
                            variant="outline"
                            className={cn(
                              "shadow-3xs whitespace-nowrap px-2.5 py-0.5 text-xs font-bold",
                              statusColors[
                                ticket.status as keyof typeof statusColors
                              ] ?? statusColors.new
                            )}
                            title={formatStatusLabel(ticket.status)}
                          >
                            {streamStatusLabel(ticket.status, isSuperAgent)}
                          </Badge>
                          {ticket.overdue && (
                            <span
                              className={cn(
                                "flex items-center gap-0.5 whitespace-nowrap rounded px-2 py-0.5 text-[10px] font-bold",
                                ticket.overdueLevel === "critical"
                                  ? "border border-red-100 bg-red-50 text-red-700"
                                  : "border border-amber-100 bg-amber-50 text-amber-700"
                              )}
                            >
                              <Clock className="h-2.5 w-2.5" />
                              {ticket.overdue}
                            </span>
                          )}
                        </div>
                      }
                    />
                    <StreamAttachmentDownloads ticket={ticket} />
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}

function Clock({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
